<template>
  <span
    v-if="link && (disabled || loading)"
    class="button button--disabled"
    :class="[
      $attrs.class,
      `button--${severity}`,
      rounded && `button--rounded`,
      loading && `button--loading`,
      hasText() && `button--has-text`,
      hasIconLeft() && `button--has-icon-left`,
      hasIconRight() && `button--has-icon-right`,
    ]"
    v-bind="$attrs"
  >
    <span class="button-inner">
      <slot name="iconLeft" />
      <slot />
      <slot name="iconRight" />
    </span>
    <NewFeatureBadge v-if="showNewFeatureBadge" />
  </span>

  <RouterLink
    v-if="link && (!disabled && !loading)"
    :to="link"
    class="button"
    :class="[
      $attrs.class,
      `button--${severity}`,
      rounded && `button--rounded`,
      loading && `button--loading`,
      hasText() && `button--has-text`,
      hasIconLeft() && `button--has-icon-left`,
      hasIconRight() && `button--has-icon-right`,
    ]"
    v-bind="$attrs"
  >
    <span class="button-inner">
      <slot name="iconLeft" />
      <slot />
      <slot name="iconRight" />
    </span>
    <NewFeatureBadge v-if="showNewFeatureBadge" />
  </RouterLink>

  <button
    v-else
    class="button"
    type="button"
    :class="[
      $attrs.class,
      `button--${severity}`,
      rounded && `button--rounded`,
      disabled && `button--disabled`,
      loading && `button--loading`,
      ignorePadding && `button--no-padding`,
      size && `button--${size}`,
      hasText() && `button--has-text`,
      hasIconLeft() && `button--has-icon-left`,
      hasIconRight() && `button--has-icon-right`,
    ]"
    :disabled="disabled || loading"
    @click="buttonClicked"
    v-bind="$attrs"
  >
    <span class="button-inner">
      <slot name="iconLeft" />
      <slot />
      <slot name="iconRight" />
    </span>
    <NewFeatureBadge v-if="showNewFeatureBadge" />
  </button>
</template>

<script setup lang="ts">
import { useSlots } from 'vue';
import { RouterLink } from 'vue-router';

import NewFeatureBadge from '@/components/ui/NewFeatureBadge.vue';

type Severity =
  | 'primary' // blue bg
  | 'secondary' // blue outline
  | 'tertiary' // transparent
  | 'pagination' // yellow for page buttons
  | 'close' // similar to pagination
  | 'tab' // for tabs
  | 'sidebar'
  | 'dropdown-item'; // for ... menus

const props = withDefaults(
  defineProps<{
    /**
     * Set the overall style of the button. Named Severity because things like "class" and "style" are reserved.
     */
    severity?: Severity;
    /**
     * If the button is disabled, typically used for forms with errors.
     */
    disabled?: boolean | undefined;
    /**
     * Loading state, typically used when the button has been clicked and the form is submitting
     */
    loading?: boolean | undefined;
    /**
     * Only used for the buttons at the top of the site. Eg: Help, Notifications
     */
    rounded?: boolean | null;
    /**
     * If the button links to another page instead of triggering an event, enter a link
     */
    link?: string;

    /**
     * "Double" is all that exists here, used for the icon-only button on File Uploads
     */
    size?: string | null;
    /**
     * If Padding should be removed, typically done when a button appears as a link inline with other text.
     */
    ignorePadding?: boolean | null;
    /**
     * If we should show the new feature badge.
     */
    showNewFeatureBadge?: boolean;

  }>(),
  {
    severity: 'primary',
    rounded: null,
    size: null,
    ignorePadding: null,
    showNewFeatureBadge: false,
  },
);

const slots = useSlots();

const hasIconRight = () => {
  return !!slots['iconRight'];
};

const hasIconLeft = () => {
  return !!slots['iconLeft'];
};

const hasText = () => {
  return !!slots['default'];
};

const emit = defineEmits(['click']);

const buttonClicked = (event: MouseEvent) => {
  if (!props.disabled) {
    emit('click', event);
  }
};
</script>

<style lang="scss">
a.button {
  @include mixins.flex-center();
}

.button {
  @include typography.font-body($size: 'md', $weight: 'normal');
  cursor: pointer;
  position: relative;

  .button-inner {
    @include mixins.flex-center($gap: variables.$sizing-xxs);

    svg {
      flex-shrink: 0;
      height: variables.$sizing-md;
      margin: 0;
      width: variables.$sizing-md;
    }
  }

  &.button--disabled {
    cursor: default;
    pointer-events: none;
  }
}

/***********************
*
* Button Padding
*
************************/

.button--has-text {
  @include sizing.padding(variables.$sizing-xxs, variables.$sizing-sm);
}

.button--has-text.button--has-icon-left {
  @include sizing.padding(
    variables.$sizing-xxs,
    variables.$sizing-sm,
    variables.$sizing-xxs,
    variables.$sizing-xs
  );
}

.button--has-text.button--has-icon-right {
  @include sizing.padding(
    variables.$sizing-xxs,
    variables.$sizing-xs,
    variables.$sizing-xxs,
    variables.$sizing-sm
  );
}

.button--has-icon-left,
.button--has-icon-right {
  @include sizing.padding(variables.$sizing-xxs);
}

.button--no-padding {
  padding: 0;
}

/***********************
*
* Button Styles
*
************************/

.button--primary {
  @include mixins.rounded-border($color: variables.$primitives-color-primary-500);
  background-color: variables.$primitives-color-primary-500;
  color: variables.$primitives-color-background-100;

  &.button--hover,
  &:hover {
    background-color: variables.$primitives-color-primary-400;
    border-color: variables.$primitives-color-primary-400;
  }

  &.button--active,
  &.button--focus,
  &:active,
  &:focus {
    background-color: variables.$primitives-color-primary-600;
    border-color: variables.$primitives-color-primary-600;
  }

  &.button--disabled {
    background-color: variables.$primitives-color-tertiary-300;
    border-color: variables.$primitives-color-tertiary-300;
  }
}

.button--secondary {
  @include mixins.rounded-border($color: variables.$primitives-color-primary-500);
  background-color: variables.$primitives-color-background-100;
  color: variables.$primitives-color-primary-500;

  &.button--hover,
  &:hover {
    background-color: variables.$primitives-color-tertiary-200;
  }

  &.button--active,
  &.button--focus,
  &:active,
  &:focus {
    background-color: variables.$primitives-color-tertiary-300;
  }

  &.button--disabled {
    background-color: variables.$primitives-color-background-100;
    border-color: variables.$primitives-color-tertiary-200;
    color: variables.$primitives-color-tertiary-400;
  }
}

.button--tertiary {
  @include mixins.rounded-border($color: transparent);
  background-color: transparent;
  color: variables.$primitives-color-primary-500;
  text-decoration: underline;

  &.button--hover,
  &:hover {
    color: variables.$primitives-color-primary-400;
  }

  &.button--active,
  &.button--focus,
  &:active,
  &:focus {
    color: variables.$primitives-color-primary-600;
  }

  &.button--disabled {
    color: variables.$primitives-color-tertiary-400;
  }
}

.button--tab {
  @include typography.font-body($size: 'sm', $weight: 'medium');
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  color: variables.$primitives-color-tertiary-400;
  text-transform: uppercase;
  transition: border-color 0.2s, color 0.2s;

  &.button--hover,
  &:hover {
    border-bottom-color: variables.$primitives-color-tertiary-500;
    color: variables.$primitives-color-tertiary-500;
  }

  &.button--active,
  &.button--focus,
  &:active,
  &:focus {
    border-bottom-color: variables.$primitives-color-primary-500;
    color: variables.$primitives-color-primary-500;
  }
}


.button--pagination {
  @include mixins.rounded-border($color: transparent, $size: variables.$sizing-xxxs);
  @include typography.font-body($size: 'sm', $weight: 'normal');
  background-color: transparent;
  color: variables.$primitives-color-tertiary-500;
  height: 24px;
  min-height: 24px;
  min-width: 24px;
  padding: 0;
  width: 24px;

  &:hover {
    background-color: variables.$primitives-color-secondary-200;
    color: variables.$primitives-color-tertiary-600;
  }

  &:focus {
    background-color: variables.$primitives-color-secondary-600;
    color: variables.$primitives-color-tertiary-600;
  }

  &.button--disabled,
  &.button--disabled:hover {
    background: transparent;
    color: variables.$primitives-color-tertiary-400;
  }

  &.pagination--active,
  &.pagination--active:hover,
  &:active {
    background-color: variables.$primitives-color-secondary-400;
    color: variables.$primitives-color-tertiary-600;
  }
}

.button--dropdown-item {
  @include mixins.rounded-border($color: transparent, $size: variables.$sizing-xxxs);
  background-color: transparent;
  color: variables.$primitives-color-tertiary-500;
  width: 100%;

  &:active {
    background-color: variables.$primitives-color-primary-500;
    border-color: variables.$primitives-color-primary-500;
    color: variables.$primitives-color-background-100;
  }

  &:hover {
    background-color: variables.$primitives-color-primary-400;
    border-color: variables.$primitives-color-primary-400;
    color: variables.$primitives-color-background-100;
  }

  &:focus {
    background-color: variables.$primitives-color-primary-600;
    border-color: variables.$primitives-color-primary-600;
    color: variables.$primitives-color-background-100;
  }

  &.button--disabled {
    background-color: transparent;
    border-color: transparent;
    color: variables.$primitives-color-tertiary-400;
  }

  .button-inner {
    justify-content: flex-start;
    width: 100%;
  }
}

.button--close {
  @include mixins.rounded-border($color: transparent);
  @include typography.font-body($size: 'sm', $weight: 'normal');
  background-color: transparent;
  border-radius: 50%;
  color: variables.$primitives-color-tertiary-500;
  height: 32px;
  min-height: 32px;
  min-width: 32px;
  padding: 0;
  width: 32px;

  &:hover {
    background-color: variables.$primitives-color-secondary-200;
    color: variables.$primitives-color-tertiary-600;
  }

  &:focus {
    background-color: variables.$primitives-color-secondary-600;
    color: variables.$primitives-color-tertiary-600;
  }

  &.button--disabled,
  &.button--disabled:hover {
    background: transparent;
    color: variables.$primitives-color-tertiary-400;
  }

  &.pagination--active,
  &.pagination--active:hover,
  &:active {
    background-color: variables.$primitives-color-secondary-400;
    color: variables.$primitives-color-tertiary-600;
  }
}

/***********************
*
* Button Shape
*
************************/

.button--rounded {
  border-radius: 50%;
  min-height: 32px;
  min-width: 32px;
  padding: 0;

  .button-inner svg {
    height: 100%;
    width: 100%;
  }
}

/***********************
*
* Button Sizing
*
************************/

.button--has-icon-left,
.button--has-icon-right {
  min-height: 42px;
  min-width: 42px;
}

.button--double {
  &.button--has-icon-left,
  &.button--has-icon-right {
    min-height: 64px;
    min-width: 64px;
  }

  .button-inner svg {
    height: variables.$sizing-xxl;
    width: variables.$sizing-xxl;
  }
}

/***********************
*
* Button Loading
*
************************/

.button--loading {
  background-color: variables.$primitives-color-tertiary-200;
  border-color: variables.$primitives-color-tertiary-200;
  color: variables.$primitives-color-tertiary-200;
  cursor: default;

  &:hover,
  &:active {
    background-color: variables.$primitives-color-tertiary-200;
    border-color: variables.$primitives-color-tertiary-200;
    color: variables.$primitives-color-tertiary-200;
  }

  .button-inner::after {
    // Almost identical to .loading-spinner but half the size
    animation: spinner 1s infinite steps(8);
    border-radius: 50%;
    box-shadow:
      calc(1 * variables.$sizing-xs) calc(0 * variables.$sizing-xs) 0 0,
      calc(0.707 * variables.$sizing-xs) calc(0.707 * variables.$sizing-xs) 0 0.5px,
      calc(0 * variables.$sizing-xs) calc(1 * variables.$sizing-xs) 0 1px,
      calc(-0.707 * variables.$sizing-xs) calc(0.707 * variables.$sizing-xs) 0 1.5px,
      calc(-1 * variables.$sizing-xs) calc(0 * variables.$sizing-xs) 0 2px,
      calc(-0.707 * variables.$sizing-xs) calc(-0.707 * variables.$sizing-xs) 0 2.5px,
      calc(0 * variables.$sizing-xs) calc(-1 * variables.$sizing-xs) 0 3px;
    color: variables.$primitives-color-tertiary-500;
    content: '';
    height: 2px;
    position: absolute;
    width: 2px;
  }
}
</style>
